<script setup>
import { computed, ref } from 'vue';
import { usePage, Link } from '@inertiajs/vue3';
import { LayoutDashboard, Users, CreditCard, Layers, Package, Tag, LogOut, Menu, Shield, ArrowLeft, Radio, Smartphone } from '@lucide/vue';
import AppLogo from '@/Components/AppLogo.vue';

const page = usePage();
const user = computed(() => page.props.auth?.user);
const isSidebarOpen = ref(false);

const effectiveRoleLabel = computed(() => {
  const roles = user.value?.roles || [];
  if (!roles.length) return 'Back Office User';
  return roles.map(r => r.replace('_', ' ').toUpperCase()).join(', ');
});
</script>

<template>
  <div class="min-h-screen bg-slate-100 text-slate-900 flex select-none">
    <!-- Sidebar Desktop -->
    <aside class="hidden lg:flex flex-col w-64 bg-slate-900 text-slate-100 border-r border-slate-800 shrink-0">
      <div class="p-5 border-b border-slate-800 flex items-center justify-between">
        <div>
          <AppLogo size="sm" />
          <span class="text-[10px] font-bold tracking-widest text-indigo-400 uppercase mt-1 block">Back-Office Portal</span>
        </div>
      </div>

      <!-- Quick Switch Back to Customer / Reseller Portal -->
      <div class="px-4 pt-4">
        <Link
          :href="route('dashboard')"
          class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-indigo-300 font-bold text-xs border border-slate-700 transition shadow-xs"
        >
          <ArrowLeft class="w-3.5 h-3.5 text-indigo-400" />
          <span>Kembali ke Portal</span>
        </Link>
      </div>

      <!-- Sidebar Navigation -->
      <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
        <Link
          :href="route('admin.dashboard')"
          class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-semibold text-xs transition"
          :class="$page.component === 'Backoffice/Dashboard' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <LayoutDashboard class="w-4 h-4" />
          <span>Dashboard Ops</span>
        </Link>

        <div class="pt-4 pb-1 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          Katalog Produk
        </div>

        <Link
          :href="route('admin.catalogue.categories.index')"
          class="flex items-center gap-3 px-3.5 py-2 rounded-xl font-medium text-xs transition"
          :class="$page.component.startsWith('Backoffice/Catalogue/Categories') ? 'bg-indigo-600 text-white font-semibold shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <Layers class="w-4 h-4" />
          <span>Kategori Produk</span>
        </Link>

        <Link
          :href="route('admin.catalogue.operators.index')"
          class="flex items-center gap-3 px-3.5 py-2 rounded-xl font-medium text-xs transition"
          :class="$page.component.startsWith('Backoffice/Catalogue/Operators') ? 'bg-indigo-600 text-white font-semibold shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <Radio class="w-4 h-4" />
          <span>Operator Seluler</span>
        </Link>

        <Link
          :href="route('admin.catalogue.operator-prefixes.index')"
          class="flex items-center gap-3 px-3.5 py-2 rounded-xl font-medium text-xs transition"
          :class="$page.component.startsWith('Backoffice/Catalogue/OperatorPrefixes') ? 'bg-indigo-600 text-white font-semibold shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <Smartphone class="w-4 h-4" />
          <span>Prefix Operator</span>
        </Link>

        <Link
          :href="route('admin.catalogue.products.index')"
          class="flex items-center gap-3 px-3.5 py-2 rounded-xl font-medium text-xs transition"
          :class="$page.component.startsWith('Backoffice/Catalogue/Products') ? 'bg-indigo-600 text-white font-semibold shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <Package class="w-4 h-4" />
          <span>Master Produk</span>
        </Link>

        <Link
          :href="route('admin.catalogue.skus.index')"
          class="flex items-center gap-3 px-3.5 py-2 rounded-xl font-medium text-xs transition"
          :class="$page.component.startsWith('Backoffice/Catalogue/Skus') ? 'bg-indigo-600 text-white font-semibold shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <Tag class="w-4 h-4" />
          <span>Master SKU</span>
        </Link>

        <div class="pt-4 pb-1 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          Manajemen Sistem
        </div>

        <Link
          :href="route('admin.users.index')"
          class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium transition"
          :class="$page.component.startsWith('Backoffice/Users') ? 'bg-indigo-600 text-white font-semibold shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'"
        >
          <Users class="w-4 h-4" />
          <span>Pengguna & Reseller</span>
        </Link>

        <div class="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-slate-400 text-xs font-medium opacity-60 cursor-not-allowed">
          <CreditCard class="w-4 h-4" />
          <span>Supplier & Routing</span>
          <span class="ml-auto text-[9px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-400">Segera</span>
        </div>
      </nav>

      <!-- User Profile Summary in Sidebar -->
      <div class="p-4 border-t border-slate-800 bg-slate-950/50">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-lg bg-indigo-600/30 text-indigo-400 border border-indigo-500/30 flex items-center justify-center font-bold text-xs">
            <Shield class="w-4 h-4" />
          </div>
          <div class="flex flex-col min-w-0 flex-1">
            <span class="text-xs font-bold text-slate-200 truncate">{{ user?.display_name || 'Petugas' }}</span>
            <span class="text-[10px] font-semibold text-indigo-400 truncate">{{ effectiveRoleLabel }}</span>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Top Header -->
      <header class="bg-white border-b border-slate-200 h-16 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30 shadow-xs">
        <div class="flex items-center gap-3">
          <button
            @click="isSidebarOpen = !isSidebarOpen"
            type="button"
            class="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
          >
            <Menu class="w-5 h-5" />
          </button>
          <div class="flex flex-col">
            <h1 class="text-sm font-bold text-slate-900">Portal Operasional Manza PPOB</h1>
            <span class="text-[11px] text-slate-500">Monitoring sistem dan pengelolaan platform</span>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <!-- Return to Customer/Reseller Portal Action -->
          <Link
            :href="route('dashboard')"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-xs font-bold transition shadow-2xs"
          >
            <ArrowLeft class="w-3.5 h-3.5 text-indigo-600" />
            <span>Kembali ke Portal</span>
          </Link>

          <div class="hidden md:flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
            <Shield class="w-3.5 h-3.5 text-indigo-600" />
            <span>Peran: {{ effectiveRoleLabel }}</span>
          </div>

          <Link
            :href="route('logout')"
            method="post"
            as="button"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-600 text-xs font-semibold text-slate-700 transition"
          >
            <LogOut class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">Keluar</span>
          </Link>
        </div>
      </header>

      <!-- Main Body -->
      <main class="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
        <slot />
      </main>

      <!-- Footer -->
      <footer class="bg-white border-t border-slate-200 py-4 px-6 text-center text-xs text-slate-500">
        &copy; 2026 Manza PPOB - Backoffice Operasional
      </footer>
    </div>
  </div>
</template>
